import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  List,
  ListItemButton,
  CircularProgress,
  Box,
  Button,
  IconButton,
  Alert,
  Stack,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import axios from 'axios';

const API_SEARCH_PRODUCTS = '/suggestedOrders/getAddProductResults';
const API_ADD_UPDATE_ITEM = '/suggestedOrders/addUpdateItem';

const SEARCH_DEBOUNCE_DELAY = 300; // milliseconds
const MAX_RENDERED_RESULTS = 250;
const CSV_HEADERS = ['upc', 'min', 'max'];

const parseCsvLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
};

const parseCsvText = (csvText) => {
  const normalizedLines = String(csvText || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (normalizedLines.length === 0) {
    throw new Error('CSV file is empty.');
  }

  const [headerLine, ...dataLines] = normalizedLines;
  const headers = parseCsvLine(headerLine).map((header) => header.toLowerCase());
  const missingHeaders = CSV_HEADERS.filter((header) => !headers.includes(header));

  if (missingHeaders.length > 0) {
    throw new Error(`CSV must include columns: ${CSV_HEADERS.join(', ')}.`);
  }

  const upcIndex = headers.indexOf('upc');
  const minIndex = headers.indexOf('min');
  const maxIndex = headers.indexOf('max');

  if (dataLines.length === 0) {
    throw new Error('CSV file does not contain any item rows.');
  }

  return dataLines.map((line, index) => {
    const columns = parseCsvLine(line);
    const upc = String(columns[upcIndex] || '').trim();
    const minRaw = String(columns[minIndex] || '').trim();
    const maxRaw = String(columns[maxIndex] || '').trim();
    const min = Number.parseInt(minRaw, 10);
    const max = Number.parseInt(maxRaw, 10);
    const rowNumber = index + 2;

    if (!upc) {
      throw new Error(`Row ${rowNumber}: UPC is required.`);
    }

    if (minRaw === '' || Number.isNaN(min) || min < 0) {
      throw new Error(`Row ${rowNumber}: Min must be a non-negative integer.`);
    }

    if (maxRaw === '' || Number.isNaN(max) || max < 0) {
      throw new Error(`Row ${rowNumber}: Max must be a non-negative integer.`);
    }

    if (max < min) {
      throw new Error(`Row ${rowNumber}: Max cannot be less than Min.`);
    }

    return {
      upc,
      min,
      max,
      rowNumber,
    };
  });
};

const normalizeUpc = (value) => String(value || '').replace(/\D/g, '');

const getDisplayDescription = (brand, description) => {
  const normalizedBrand = String(brand || '').trim();
  const normalizedDescription = String(description || '').trim();

  if (!normalizedBrand || !normalizedDescription) {
    return normalizedDescription;
  }

  const escapedBrand = normalizedBrand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const leadingBrandPattern = new RegExp(`^${escapedBrand}[\\s,:-]*`, 'i');
  return normalizedDescription.replace(leadingBrandPattern, '').trim() || normalizedDescription;
};

const AddItemModal = ({ open, onClose, token, onItemsChanged }) => {
  const theme = useTheme();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [selectedItem, setSelectedItem] = useState(null);
  const [minQty, setMinQty] = useState('');
  const [maxQty, setMaxQty] = useState('');
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [addError, setAddError] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importRows, setImportRows] = useState([]);
  const [importError, setImportError] = useState('');
  const [importSummary, setImportSummary] = useState(null);
  const [loadingImport, setLoadingImport] = useState(false);
  const latestSearchRequestIdRef = useRef(0);
  const resetTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  const resetModalState = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSearchResults([]);
    setLoadingSearch(false);
    setLoadingAdd(false);
    setSearchError(null);
    setAddError(null);
    setSelectedItem(null);
    setMinQty('');
    setMaxQty('');
    setValidationError('');
    setImportFile(null);
    setImportRows([]);
    setImportError('');
    setImportSummary(null);
    setLoadingImport(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const trimmedSearchTerm = debouncedSearchTerm.trim();
    if (!trimmedSearchTerm) {
      setSearchResults([]);
      setSearchError(null);
      setLoadingSearch(false);
      return undefined;
    }

    const requestId = latestSearchRequestIdRef.current + 1;
    latestSearchRequestIdRef.current = requestId;
    const abortController = new AbortController();

    setLoadingSearch(true);
    setSearchError(null);
    setSelectedItem(null);
    setAddError(null);

    const fetchSearchResults = async () => {
      try {
        const response = await axios.post(
          API_SEARCH_PRODUCTS,
          {
            searchTerm: trimmedSearchTerm,
          },
          { signal: abortController.signal },
        );

        if (latestSearchRequestIdRef.current !== requestId) {
          return;
        }
        setSearchResults(response.data || []);
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') {
          return;
        }
        if (latestSearchRequestIdRef.current !== requestId) {
          return;
        }
        console.error('Search error:', err);
        setSearchError(err.response?.data?.message || 'Failed to fetch results. Please try again.');
        setSearchResults([]);
      } finally {
        if (latestSearchRequestIdRef.current === requestId) {
          setLoadingSearch(false);
        }
      }
    };

    fetchSearchResults();

    return () => {
      abortController.abort();
    };
  }, [debouncedSearchTerm, open]);

  useEffect(() => {
    const min = parseInt(minQty, 10);
    const max = parseInt(maxQty, 10);

    if (!isNaN(min) && !isNaN(max) && max < min) {
      setValidationError('Max Qty cannot be less than Min Qty.');
    } else {
      setValidationError('');
    }
  }, [minQty, maxQty]);

  const isAddButtonDisabled = useMemo(() => {
    const min = parseInt(minQty, 10);
    const max = parseInt(maxQty, 10);
    return (
      loadingAdd ||
      !minQty ||
      !maxQty ||
      isNaN(min) ||
      isNaN(max) ||
      min < 0 ||
      max < 0 ||
      max < min
    );
  }, [minQty, maxQty, loadingAdd]);

  const displayedSearchResults = useMemo(
    () => searchResults.slice(0, MAX_RENDERED_RESULTS),
    [searchResults],
  );

  const handleSelectItem = useCallback((item) => {
    setSelectedItem(item);
    setMinQty('');
    setMaxQty('');
    setAddError(null);
    setValidationError('');
  }, []);

  const handleAddItem = useCallback(async () => {
    if (!selectedItem || isAddButtonDisabled) return;

    setLoadingAdd(true);
    setAddError(null);

    try {
      await axios.post(API_ADD_UPDATE_ITEM, {
        customerNumber: token,
        itemNumber: selectedItem.ss_item,
        itemMin: parseInt(minQty, 10),
        itemMax: parseInt(maxQty, 10),
      });

      setSelectedItem(null);
      setMinQty('');
      setMaxQty('');
      await onItemsChanged?.();
    } catch (err) {
      console.error('Add item error:', err);
      setAddError(err.response?.data?.message || 'Failed to add item. Please try again.');
    } finally {
      setLoadingAdd(false);
    }
  }, [selectedItem, token, minQty, maxQty, isAddButtonDisabled, onItemsChanged]);

  const handleImportFileSelection = useCallback(async (event) => {
    const selectedFile = event.target.files?.[0];

    setImportFile(null);
    setImportRows([]);
    setImportError('');
    setImportSummary(null);

    if (!selectedFile) {
      return;
    }

    try {
      const fileText = await selectedFile.text();
      const parsedRows = parseCsvText(fileText);
      setImportFile(selectedFile);
      setImportRows(parsedRows);
    } catch (error) {
      setImportError(error.message || 'Failed to parse CSV file.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, []);

  const handleImportCsv = useCallback(async () => {
    if (!importRows.length || loadingImport) {
      return;
    }

    setLoadingImport(true);
    setImportError('');
    setImportSummary(null);
    setAddError(null);

    let importedCount = 0;
    const failedRows = [];

    for (const row of importRows) {
      try {
        const searchResponse = await axios.post(API_SEARCH_PRODUCTS, {
          searchTerm: row.upc,
        });

        const matchedItem = (searchResponse.data || []).find(
          (result) => normalizeUpc(result.upc) === normalizeUpc(row.upc),
        );

        if (!matchedItem?.ss_item) {
          failedRows.push(`Row ${row.rowNumber}: UPC ${row.upc} was not found.`);
          continue;
        }

        await axios.post(API_ADD_UPDATE_ITEM, {
          customerNumber: token,
          itemNumber: matchedItem.ss_item,
          itemMin: row.min,
          itemMax: row.max,
        });

        importedCount += 1;
      } catch (error) {
        console.error('CSV import row failed:', error);
        failedRows.push(
          `Row ${row.rowNumber}: ${error.response?.data?.message || `Unable to import UPC ${row.upc}.`}`,
        );
      }
    }

    if (importedCount > 0) {
      await onItemsChanged?.();
    }

    setImportSummary({
      importedCount,
      failedRows,
    });
    setLoadingImport(false);
  }, [importRows, loadingImport, onItemsChanged, token]);

  useEffect(() => {
    if (open) {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
      return undefined;
    }

    resetTimerRef.current = setTimeout(() => {
      resetModalState();
    }, 300);

    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, [open, resetModalState]);

  const handleInternalClose = useCallback(() => {
    latestSearchRequestIdRef.current += 1;
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleInternalClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          borderRadius: 2.5,
          border: `1px solid ${theme.palette.primary.main}2e`,
          boxShadow: `0 14px 34px ${theme.palette.primary.main}1f`,
          backgroundImage: `linear-gradient(180deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.paper} 34%)`,
        },
      }}
      aria-labelledby='add-item-dialog-title'
    >
      <DialogTitle
        id='add-item-dialog-title'
        sx={{
          position: 'relative',
          pr: 6,
          pb: 1.2,
          borderBottom: `1px solid ${theme.palette.primary.main}26`,
          backgroundColor: `${theme.palette.primary.main}12`,
        }}
      >
        <Stack direction='row' alignItems='center'>
          <Typography variant='h6' component='div' sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            Add Item to Min/Max List
          </Typography>
        </Stack>
        <Typography variant='body2' color='text.secondary'>
          Search products and add quantity thresholds.
        </Typography>
        <IconButton
          aria-label='close'
          onClick={handleInternalClose}
          size='small'
          sx={{ position: 'absolute', top: 12, right: 12, p: 0.5 }}
        >
          <CloseIcon sx={{ color: theme.palette.delete.main, fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent='space-between'
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.primary.main}26`,
              backgroundColor: `${theme.palette.primary.main}08`,
            }}
          >
            <Box>
              <Typography variant='subtitle2' sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                Bulk Import Min/Max CSV
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Upload a CSV with `UPC`, `Min`, and `Max` columns.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <input
                ref={fileInputRef}
                type='file'
                accept='.csv,text/csv'
                hidden
                onChange={handleImportFileSelection}
              />
              <Button
                variant='outlined'
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loadingImport}
              >
                Import CSV
              </Button>
              {importRows.length > 0 ? (
                <Button
                  variant='contained'
                  onClick={handleImportCsv}
                  disabled={loadingImport}
                  startIcon={loadingImport ? <CircularProgress size={18} color='inherit' /> : null}
                >
                  {loadingImport ? 'Importing...' : 'Confirm Import'}
                </Button>
              ) : null}
            </Stack>
          </Stack>

          {importFile ? (
            <Alert severity='info'>
              {`${importFile.name} selected. ${importRows.length} row${importRows.length === 1 ? '' : 's'} ready to import.`}
            </Alert>
          ) : null}

          {importError ? <Alert severity='error'>{importError}</Alert> : null}

          {importSummary ? (
            <Alert severity={importSummary.failedRows.length > 0 ? 'warning' : 'success'}>
              <Box>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  Imported {importSummary.importedCount} item{importSummary.importedCount === 1 ? '' : 's'}.
                </Typography>
                {importSummary.failedRows.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    {importSummary.failedRows.slice(0, 5).map((message) => (
                      <Typography key={message} variant='body2'>
                        {message}
                      </Typography>
                    ))}
                    {importSummary.failedRows.length > 5 ? (
                      <Typography variant='body2'>
                        {`${importSummary.failedRows.length - 5} more row errors not shown.`}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            </Alert>
          ) : null}

          <Divider />

          <TextField
            fullWidth
            variant='outlined'
            placeholder='Search by Item #, Description, Mfr SKU, UPC, Brand...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />

          {loadingSearch && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {searchError && <Alert severity='error'>{searchError}</Alert>}

          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              maxHeight: 'calc(85vh - 200px)',
            }}
          >
            {!loadingSearch && searchResults.length > 0 && (
              <List disablePadding>
                {displayedSearchResults.map((result) => {
                  const displayDescription = getDisplayDescription(result.brand, result.description);

                  return (
                  <React.Fragment key={result.ss_item}>
                    <ListItemButton
                      selected={selectedItem?.ss_item === result.ss_item}
                      onClick={() => handleSelectItem(result)}
                      sx={{
                        mb: 1,
                        borderRadius: 1.5,
                        border: `1px solid ${
                          selectedItem?.ss_item === result.ss_item
                            ? theme.palette.primary.main
                            : theme.palette.divider
                        }`,
                        backgroundColor:
                          selectedItem?.ss_item === result.ss_item
                            ? `${theme.palette.primary.main}10`
                            : theme.palette.background.paper,
                        alignItems: 'flex-start',
                        px: 1.5,
                        py: 1.25,
                        '&:hover': {
                          backgroundColor:
                            selectedItem?.ss_item === result.ss_item
                              ? `${theme.palette.primary.main}14`
                              : theme.palette.action.hover,
                        },
                      }}
                    >
                      <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Typography
                          variant='subtitle2'
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            mb: 0.25,
                            pr: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {result.brand || 'Unknown Brand'}
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.35,
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {displayDescription}
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                            gap: 0.75,
                          }}
                        >
                          <Box>
                            <Typography variant='caption' color='text.secondary'>
                              SS Item
                            </Typography>
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>
                              {result.ss_item || 'N/A'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant='caption' color='text.secondary'>
                              Mfr SKU
                            </Typography>
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>
                              {result.mfr_sku || 'N/A'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant='caption' color='text.secondary'>
                              UPC
                            </Typography>
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>
                              {result.upc || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </ListItemButton>
                    {selectedItem?.ss_item === result.ss_item && (
                      <Box
                        sx={{
                          p: 2,
                          pl: 4,
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1.5,
                          alignItems: { sm: 'center' },
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <TextField
                          label='Min Qty'
                          type='number'
                          value={minQty}
                          onChange={(e) => setMinQty(e.target.value)}
                          size='small'
                          required
                          error={
                            !!validationError ||
                            (!isNaN(parseInt(minQty, 10)) && parseInt(minQty, 10) < 0)
                          }
                          helperText={
                            !isNaN(parseInt(minQty, 10)) && parseInt(minQty, 10) < 0
                              ? 'Min > 0'
                              : ''
                          }
                          inputProps={{ min: 0 }}
                          sx={{ width: { xs: '100%', sm: 100 } }}
                          disabled={loadingAdd}
                        />
                        <TextField
                          label='Max Qty'
                          type='number'
                          value={maxQty}
                          onChange={(e) => setMaxQty(e.target.value)}
                          size='small'
                          required
                          error={
                            !!validationError ||
                            (!isNaN(parseInt(maxQty, 10)) && parseInt(maxQty, 10) < 0)
                          }
                          helperText={
                            validationError ||
                            (!isNaN(parseInt(maxQty, 10)) && parseInt(maxQty, 10) < 0
                              ? 'Max > 0'
                              : '')
                          }
                          inputProps={{ min: 0 }}
                          sx={{ width: { xs: '100%', sm: 100 } }}
                          disabled={loadingAdd}
                        />
                        <Button
                          variant='contained'
                          color='primary'
                          onClick={handleAddItem}
                          disabled={isAddButtonDisabled}
                          startIcon={
                            loadingAdd ? <CircularProgress size={20} color='inherit' /> : null
                          }
                          sx={{ flexShrink: 0, height: 40, width: { xs: '100%', sm: 'auto' } }}
                        >
                          {loadingAdd ? 'Adding...' : 'Add Item'}
                        </Button>

                        {addError && selectedItem?.ss_item === result.ss_item && (
                          <Alert
                            severity='error'
                            sx={{ width: '100%', mt: { xs: 1, sm: 0 }, ml: { sm: 1 } }}
                          >
                            {addError}
                          </Alert>
                        )}
                      </Box>
                    )}
                  </React.Fragment>
                  );
                })}
              </List>
            )}

            {!loadingSearch && searchResults.length > MAX_RENDERED_RESULTS && (
              <Alert severity='info' sx={{ mt: 1 }}>
                Showing first {MAX_RENDERED_RESULTS.toLocaleString()} of{' '}
                {searchResults.length.toLocaleString()} results. Refine your search for faster interaction.
              </Alert>
            )}

            {!loadingSearch &&
              !searchError &&
              debouncedSearchTerm.trim() &&
              searchResults.length === 0 && (
                <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                  No results found for "{debouncedSearchTerm}".
                </Typography>
              )}

            {!loadingSearch && !debouncedSearchTerm.trim() && (
              <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                Enter a search term to find products.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default AddItemModal;
